'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * Sebastian Bust 3D Model with AR Support
 * Interactive 3D model viewer with AR capabilities
 */
export function SebastianBust({ className, showARButton = true, autoRotate = true }) {
  const modelRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Dynamically load model-viewer script
    if (typeof window !== 'undefined' && !window.customElements.get('model-viewer')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
      document.head.appendChild(script);
    }

    const model = modelRef.current;
    if (!model) return;

    // Event listeners
    const handleLoad = () => {
      setIsLoading(false);
      setError(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setError(true);
    };

    model.addEventListener('load', handleLoad);
    model.addEventListener('error', handleError);

    return () => {
      model.removeEventListener('load', handleLoad);
      model.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <div className={cn('relative w-full h-full', className)}>
      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-accent-dark border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">Lade 3D-Modell...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
          <div className="text-center p-6">
            <p className="text-gray-600 dark:text-gray-300">3D-Modell konnte nicht geladen werden</p>
          </div>
        </div>
      )}

      {/* Model Viewer */}
      <model-viewer
        ref={modelRef}
        src="/3d-models/sebastian-nawrot-bust.glb"
        poster="/3d-models/sebastian-nawrot-poster.webp"
        alt="Sebastian Nawrot - 3D Bust"
        camera-controls
        tone-mapping="neutral"
        shadow-intensity="2"
        exposure="0.42"
        ar={showARButton}
        ar-modes="webxr scene-viewer quick-look"
        ar-scale="auto"
        min-camera-orbit="-10deg 87deg 5.4m" 
        max-camera-orbit="10deg 99deg auto" 
        min-field-of-view="30deg" 
        camera-target="0.0201m 0.3578m -3m"
        class={cn(
          'w-full h-full min-h-[60vh] rounded-lg',
          isLoading && 'opacity-0'
        )}
        style={{
          width: '100%',
          height: '100%',
          minHeight: '600x',
          background: 'transparent'
        }}
      >
        {/* AR Button */}
        {showARButton && (
          <button
            slot="ar-button"
            className="ar-button bg-accent-dark hover:bg-accent text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>In AR ansehen</span>
          </button>
        )}

        {/* Loading Progress Bar */}
        <div slot="progress-bar" className="progress-bar">
          <div className="update-bar bg-accent-dark h-1"></div>
        </div>
      </model-viewer>

      {/* Info Badge */}
      <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          Interaktiv & AR-fähig
        </p>
      </div>

      <style jsx>{`
        model-viewer {
          --progress-bar-color: #6d5436;
        }

        .ar-button {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
        }

        .progress-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .update-bar {
          width: 0%;
          height: 100%;
          transition: width 0.3s;
        }
      `}</style>
    </div>
  );
}

/**
 * Meet Sebastian Card - Prominent AR Feature Card
 */
export function MeetSebastianCard({ className }) {
  return (
    <div className={cn('bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden', className)}>
      {/* Header */}
      <div className="bg-gradient-to-r from-accent-dark to-accent p-6 text-white">
        <h3 className="font-heading font-bold text-2xl mb-2">
          Meet Sebastian in 3D
        </h3>
        <p className="text-white/90 text-sm">
          Interaktives 3D-Modell mit Augmented Reality
        </p>
      </div>

      {/* 3D Viewer */}
      <div className="p-6">
        <SebastianBust className="h-[500px]" showARButton={true} autoRotate={true} />
      </div>

      {/* Instructions */}
      <div className="px-6 pb-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
            <span className="text-accent-dark font-bold">1</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Rotieren & Zoomen</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Klicken & ziehen zum Drehen, Pinch zum Zoomen</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
            <span className="text-accent-dark font-bold">2</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">AR-Modus (Mobile)</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Button klicken um mich in deine Umgebung zu stellen</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
            <span className="text-accent-dark font-bold">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Coming Soon: AI Chat</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Bald kannst du direkt mit mir chatten! 🤖</p>
          </div>
        </div>
      </div>
    </div>
  );
}
export default SebastianBust;